import React from 'react';
import { Card, Panel, ConfirmModal, Table } from '@/components/Bicester';
import { ColumnConfig } from '@/components/Bicester/Table';
import { Alert, IconButton } from 'rsuite';
import { FormikProvider, useFormik } from 'formik';
import { defaultInfo, orderStatus, ORDER_STATUS } from './constants';
import { Form } from '@/components/formik-rsuite';
import Image from '@/components/Bicester/Image';
import useContentHeight from '@/hooks/useContentHeight';
import { UserNameCell, LevelCell, Cell, OperationCell } from '@/components/Bicester/Table/Cells';
import RegisterCell from './RegisterCell';
import { useModal } from '@ebay/nice-modal-react';
import { usePersistFn, useRequest } from 'ahooks';
import { RegisterModal } from './RegisterCell';
import { Bicester } from '@/constants';
import useTable from '@/hooks/useTable';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { fetchPick, fetchShoppingList } from '@/services/subscribe/shopping';
import _ from 'lodash';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import { SERVICE_STATUS } from '@/services/constants';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';

const Shopping = () => {
  const { profile } = useReadyInfo();
  const modal = useModal(ConfirmModal);

  const {
    run: getList,
    data: { content_list: tableData, current_page, total },
    loading: getListLoading,
  } = useRequest<any>(fetchShoppingList, {
    initialData: DEFAULT_TABLE_VALUE,
    defaultParams: _.omit(_.pickBy(defaultInfo), 'time_range'),
  });

  const { onChange, onSearch, onRefresh } = useTable(getList, {
    start_at: defaultInfo.start_at,
    end_at: defaultInfo.end_at,
  });

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true,
    onSubmit(values: any, actions) {
      const { time_range = [], ...rest } = values;
      const params = {
        ...rest,
        start_at: time_range[0] || '',
        end_at: time_range[1] || '',
      };
      const nextParams = Object.assign(
        {},
        _.pickBy(params),
        _.isNumber(params?.status) && {
          status: params.status,
        }
      );
      onSearch(nextParams);
    },
  });

  const renderClaimContent = usePersistFn((rowData) => {
    return (
      <>
        <p>
          {rowData?.user_name_no_hide ? `客户名 ${rowData?.user_name_no_hide}` : ''}
          {rowData?.phone_no_hide ? ` 手机号 ${rowData?.phone_no_hide}` : ''}
        </p>
        <p>
          免提码 {rowData?.code} 包裹数 {rowData?.bags_number || 0}
        </p>
        <p>是否确认顾客取货</p>
      </>
    );
  });

  const operationHandle = usePersistFn((rowData) => {
    if (rowData?.status !== ORDER_STATUS.COMPLTED) {
      const onClick = () => {
        modal.show({
          contentText: renderClaimContent(rowData),
          submitText: '确认',
          cancelText: '取消',
          onSubmit: async () => {
            const { status } = await fetchPick({
              handsfree_id: rowData?.id,
            });
            if (status === SERVICE_STATUS.SUCCESS) {
              Alert.success('取货成功');
              onRefresh();
            }
          },
        });
      };
      return [
        {
          name: '确认顾客取货',
          show: rowData?.status !== ORDER_STATUS.CANCEL,
          onClick,
        },
      ];
    } else {
      return [{ name: '已取货', readonly: true }];
    }
  });

  const columns: ColumnConfig[] = [
    {
      columnProps: { flexGrow: 1 },
      header: '预约服务日期',
      cell: <Cell dataKey="day" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '客户名',
      cell: <UserNameCell dataKey="user_name" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '手机尾号',
      cell: <Cell dataKey="phone" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '会员等级',
      cell: <LevelCell dataKey="level" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '订单状态',
      cell: <Cell dataKey="status_name" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '购物袋总数',
      cell: <Cell>{(rowData) => rowData?.bags_number || 0}</Cell>,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '购物袋登记',
      cell: <RegisterCell />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '顾客是否提货',
      cell: <Cell>{(rowData) => (rowData?.status === ORDER_STATUS.COMPLTED ? '是' : '否')}</Cell>,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '免提码',
      cell: <Cell dataKey="code" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '取货操作',
      cell: <OperationCell operations={operationHandle} />,
    },
  ];

  return (
    <div>
      <Panel dividerHeight={10} leftShadow title="免提购物预约查询">
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex items-center">
                <Card.Input label="免提码" formKey="code" labelWeight={3} />
                <Card.Input label="手机号" formKey="phone" labelWeight={3} />
                <Card.Input label="会员号" formKey="user_code" labelWeight={3} />
                <Card.Picker
                  label="订单状态"
                  pickerData={orderStatus}
                  formKey="status"
                  labelWeight={3}
                />
                <Card.DateRangePicker
                  label="预约日期"
                  formKey="time_range"
                  placement="bottomEnd"
                  labelWeight={3}
                />
                <IconButton type="submit" className="inline-flex items-center w-[30px] ml-[10px]">
                  <Image src="/resources/images/match.svg" />
                </IconButton>
              </div>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title={`${Bicester[profile?.shopping_village]} | 免提预约`}
        subTitle={`总计${total}条`}
        leftShadow
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 169px), auto)',
        }}
      >
        <Table
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          loading={getListLoading}
          pageInfo={{ page: current_page, total }}
          onChange={onChange}
          data={tableData}
          columns={columns}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
      <RegisterModal id="register-modal" onRefresh={onRefresh} />
    </div>
  );
};

export default Shopping;
